import React, { useEffect, useState, useRef } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { PostServiceCaller, GetServiceCaller } from '../../../services/serviceCaller';
import { API_URL } from '../../../constants';
import CRDynamic_PR_table from '../../../components/CRDynamic_PR_table';
import { useDispatch } from 'react-redux';
import { updateNavbarHeading } from '../../../constants/layout/store/filterSlice';
import moment from 'moment';

const REQ_ID_KEYS = new Set(['Req ID', 'Requisition ID', 'RequisitionID', 'RequisitionSubID']);

// Column config outside component (never recreated on re-renders) ──────────
const COLUMN_LABELS = {
    CandidateID      : 'Candidate ID',      RequisitionID    : 'Requisition ID',
    SourcingDate     : 'Sourcing Date',      SourceFamily     : 'Source Family',
    Source           : 'Source',             Recruiter        : 'Recruiter',
    Name             : 'Name',              Experience       : 'Experience',
    Phone            : 'Phone',             Email            : 'Email',
    LatestOrg        : 'Latest Org',         JobFamily        : 'Job Family',
    Skills           : 'Skills',            NoticePeriod     : 'Notice Period',
    CurrentSalary    : 'Current Salary',     ExpectedSalary   : 'Expected Salary',
    HikePercent      : 'Hike %',            ScreeningDate    : 'Screening Date',
    InterviewDate1   : 'Interview Date 1',   InterviewDate2   : 'Interview Date 2',
    InterviewDate3   : 'Interview Date 3',   Status           : 'Status',
    OfferDate        : 'Offer Date',         JoiningDate      : 'Joining Date',
    RecruiterRemarks : 'Recruiter Remarks',  R1Ageing         : 'R1 Ageing',
    R2Ageing         : 'R2 Ageing',          R3Ageing         : 'R3 Ageing',
};

const INITIAL_COLUMNS = Object.fromEntries(
    Object.entries(COLUMN_LABELS).map(([key, text]) => [
        key, { text, align: 'left', visible: true, width: REQ_ID_KEYS.has(key) ? '145px' : undefined }
    ])
);

// Auto-derive: every column visible by default
const INITIAL_DEFAULT_COLUMNS = Object.fromEntries(
    Object.keys(INITIAL_COLUMNS).map(k => [k, true])
);

const TAReport = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(updateNavbarHeading("Requirement Tracker Report"));
    }, [dispatch]);

    const [reportData,    setReportData]  = useState([]);
    const [apiStatus,     setApiStatus]   = useState(undefined);
    const [searchTerm,    setSearchTerm]  = useState("");
    const [filterFormData, setFilterData] = useState([
        { label: "From Date", value: moment().subtract(30, 'days').format('DD-MMM-YYYY') },
        { label: "To Date",   value: moment().format('DD-MMM-YYYY') },
    ]);
    const [headerOffset,  setHeaderOffset] = useState(0);
    const [bottomOffset,  setBottomOffset] = useState(0);
    const wrapperRef = useRef(null);

    // Filter dropdown options
    const [statusOptions,    setStatusOptions]    = useState([]);
    const [reqIdOptions,     setReqIdOptions]     = useState([]);
    const [recruiterOptions, setRecruiterOptions] = useState([]);
    const [skillOptions,     setSkillOptions]     = useState([]);

    // Measure thead bottom + pagination top so overlay covers only the tbody area
    useEffect(() => {
        const measure = () => {
            if (!wrapperRef.current) return;
            const thead        = wrapperRef.current.querySelector('thead');
            const tableWrapper = wrapperRef.current.querySelector('.table-responsive');
            if (thead && tableWrapper) {
                const wrapperRect        = wrapperRef.current.getBoundingClientRect();
                const theadBottom        = thead.getBoundingClientRect().bottom;
                const tableWrapperBottom = tableWrapper.getBoundingClientRect().bottom;
                setHeaderOffset(Math.round(theadBottom        - wrapperRect.top));
                setBottomOffset(Math.round(wrapperRect.bottom - tableWrapperBottom));
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [apiStatus]);

    // Fetch all filter dropdown options on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [statusRes, reqRes, hrRes, skillRes] = await Promise.all([
                    GetServiceCaller(`${API_URL}fetch-candidate-status`),
                    GetServiceCaller(`${API_URL}get-open-requisitions`),
                    GetServiceCaller(`${API_URL}fetch-hr`),
                    GetServiceCaller(`${API_URL}skills`),
                ]);
                if (statusRes?.status === 200)
                    setStatusOptions([{ value: "", label: "Select Status" },
                        ...(statusRes.data || []).map(o => ({ value: o.value, label: o.text }))]);
                if (reqRes?.status === 200)
                    setReqIdOptions([{ value: "", label: "Select Requisition" },
                        ...(reqRes.data?.data || []).map(o => ({ value: o.JobDescriptionID, label: o.RequisitionID || o.Title }))]);
                if (hrRes?.status === 200)
                    setRecruiterOptions([{ value: "", label: "Select Recruiter" },
                        ...(hrRes.data?.Hr || []).map(o => ({ value: o.value, label: o.text }))]);
                if (skillRes?.status === 200)
                    setSkillOptions([{ value: "", label: "Select Skill" },
                        ...(skillRes.data || []).map(o => ({ value: o.ID, label: o.Name }))]);
            } catch (error) {
                console.error("Failed to fetch filters", error);
            }
        };
        fetchFilters();
    }, []);

    
    const fetchReportWithFilters = async (filters) => {
        setApiStatus(undefined);
        setReportData([]);
        try {
            const getValue = (label) => filters.find(o => o.label === label)?.value;
            const fDateRaw = getValue('From Date');
            const tDateRaw = getValue('To Date');

            const response = await PostServiceCaller(`${API_URL}ta-report`, {
                FromDate     : fDateRaw ? moment(fDateRaw).format('YYYY-MM-DD') : '',
                ToDate       : tDateRaw ? moment(tDateRaw).format('YYYY-MM-DD') : '',
                Status       : getValue('Status')        || '',
                RequisitionID: getValue('Requisition ID') || '',
                Recruiter    : getValue('Recruiter')      || '',
                Skill        : getValue('Skills')         || '',
            }, 'POST');

            setApiStatus(response?.status);
            setReportData(response?.status === 200 ? (response.data.data || []) : []);
        } catch (error) {
            console.error(error);
            setApiStatus(500);
        }
    };

    useEffect(() => {
        fetchReportWithFilters(filterFormData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Columns: fall back to INITIAL_COLUMNS while data is loading ──────────
    const [tableColumns, setTableColumns]               = useState(INITIAL_COLUMNS);
    const [defaultTableColumns, setDefaultTableColumns] = useState(INITIAL_DEFAULT_COLUMNS);

    useEffect(() => {
        if (!reportData?.length) return;
        const tCols = {};
        const dCols = {};
        Object.keys(reportData[0]).forEach(key => {
            tCols[key] = { text: COLUMN_LABELS[key] || key, align: 'left', visible: true, width: REQ_ID_KEYS.has(key) ? '145px' : undefined };
            dCols[key] = true;
        });
        setTableColumns(tCols);
        setDefaultTableColumns(dCols);
    }, [reportData]);

    const handleChange = (e, name) => {
        const val = e?.value !== undefined ? e.value : e;
        setFilterData(prev => {
            const idx = prev.findIndex(item => item.label === name);
            return idx !== -1
                ? prev.map((item, i) => i === idx ? { ...item, value: val } : item)
                : [...prev, { label: name, value: val }];
        });
    };

    const handleReset = () => {
        setFilterData([]);
        fetchReportWithFilters([]);
    };

    const formDatas = [
        { name: "From Date",      type: "Date" },
        { name: "To Date",        type: "Date" },
        { name: "Status",         type: "Select", options: statusOptions },
        { name: "Requisition ID", type: "Select", options: reqIdOptions },
        { name: "Recruiter",      type: "Select", options: recruiterOptions },
        { name: "Skills",         type: "Select", options: skillOptions },
    ];

    return (
        <div className="container-fluid" style={{ backgroundColor: "#F2F5FA", padding: "15px", height: "100vh" }}>
            <div className="card border-0 shadow-sm rounded-4" style={{ backgroundColor: "#FFFFFF", overflow: "hidden", height: "100%" }}>
                <div className="card-body p-3">
                    {/* position:relative anchors the overlay to this box, not the wide table */}
                    <div style={{ position: 'relative' }} ref={wrapperRef}>
                        <CRDynamic_PR_table
                            data={reportData}
                            columns={tableColumns}
                            defaultColumns={defaultTableColumns}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            isFilterRequired={true}
                            formDatas={formDatas}
                            handleChange={handleChange}
                            filterFormData={filterFormData}
                            handleGetData={() => fetchReportWithFilters(filterFormData)}
                            handleReset={handleReset}
                            isApiStatus={apiStatus}
                        />

                        {/* Overlay: visible only while API call is in progress */}
                        {apiStatus === undefined && (
                            <div style={{
                                position       : 'absolute',
                                top            : headerOffset || 0,
                                left           : 0,
                                right          : 0,
                                bottom         : bottomOffset || 0,
                                display        : 'flex',
                                flexDirection  : 'column',
                                alignItems     : 'center',
                                justifyContent : 'center',
                                backgroundColor: 'rgba(255,255,255,0.75)',
                                zIndex         : 10,
                                pointerEvents  : 'none'
                            }}>
                                <FaSpinner className="spinner" />
                                <span className="mt-2">Loading</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TAReport;
